import type RAPIER_NS from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import type { Physics } from './physics';
import { impactSpeed } from '../audio/mixLogic';

/** Car dimensions and handling (metres, kg, newtons, seconds). Forward is +x in the car's frame. */
export const CAR = {
  half: { x: 2.1, y: 0.35, z: 0.9 },
  mass: 1200,
  /** Centre of mass below the body centre, so it doesn't roll over in turns. */
  comY: -0.3,
  wheelX: 1.35,
  wheelZ: 0.82,
  wheelY: -0.15,
  wheelRadius: 0.34,
  rest: 0.25,
  stiffness: 30,
  compression: 4,
  relaxation: 5,
  friction: 2.2,
  engine: 3200,
  reverse: 1800,
  brake: 45,
  topSpeed: 32,
  steer: 0.55,
  steerRate: 2.4,
};

export interface CarInput {
  throttle: number; // -1 … 1
  steer: number; // -1 (right) … 1 (left)
  handbrake: boolean;
}

/** A car: dynamic chassis + Rapier's raycast vehicle controller (4 wheels, rear-wheel drive). */
export class Car {
  readonly body: RAPIER_NS.RigidBody;
  private readonly vc: RAPIER_NS.DynamicRayCastVehicleController;
  private steerAngle = 0;
  readonly mesh: THREE.Group;
  private readonly wheels: THREE.Object3D[] = [];
  private readonly prevP = new THREE.Vector3();
  private readonly prevQ = new THREE.Quaternion();
  private readonly curP = new THREE.Vector3();
  private readonly curQ = new THREE.Quaternion();
  private readonly lastVel: [number, number, number] = [0, 0, 0];
  /** Tail and reversing lights (their colours change with braking and reverse). */
  private readonly tailMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(5, 0.12, 0.08), toneMapped: false });
  private readonly reverseMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0.2, 0.2, 0.2), toneMapped: false });
  private braking = false;
  private reversing = false;
  /** Off while parked (engine off): no lit tail or brake lights. */
  lights = true;
  private impactMax = 0;

  constructor(
    private readonly phys: Physics,
    at: THREE.Vector3,
    yaw: number,
    /** A car model (models/library.ts, wheels on y = 0) drawn instead of the built-in body. */
    model?: { geometry: THREE.BufferGeometry; material: THREE.Material },
  ) {
    const R = phys.R;
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    this.body = phys.world.createRigidBody(
      R.RigidBodyDesc.dynamic()
        .setTranslation(at.x, at.y, at.z)
        .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w })
        .setCcdEnabled(true)
        .setLinearDamping(0.05)
        .setAngularDamping(0.4),
    );
    const { x: hx, y: hy, z: hz } = CAR.half;
    const m = CAR.mass;
    const inertia = { x: (m * (4 * hy * hy + 4 * hz * hz)) / 12, y: (m * (4 * hx * hx + 4 * hz * hz)) / 12, z: (m * (4 * hx * hx + 4 * hy * hy)) / 12 };
    const col = phys.world.createCollider(
      R.ColliderDesc.cuboid(hx, hy, hz).setMassProperties(m, { x: 0, y: CAR.comY, z: 0 }, inertia, { x: 0, y: 0, z: 0, w: 1 }).setFriction(0.3),
      this.body,
    );
    phys.tags.set(col.handle, 'dynamic');
    this.vc = phys.world.createVehicleController(this.body);
    for (const [sx, sz] of [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ]) {
      this.vc.addWheel({ x: sx * CAR.wheelX, y: CAR.wheelY, z: sz * CAR.wheelZ }, { x: 0, y: -1, z: 0 }, { x: 0, y: 0, z: 1 }, CAR.rest, CAR.wheelRadius);
    }
    for (let i = 0; i < 4; i++) {
      this.vc.setWheelSuspensionStiffness(i, CAR.stiffness);
      this.vc.setWheelSuspensionCompression(i, CAR.compression);
      this.vc.setWheelSuspensionRelaxation(i, CAR.relaxation);
      this.vc.setWheelMaxSuspensionForce(i, m * 40);
      this.vc.setWheelMaxSuspensionTravel(i, 0.3);
      this.vc.setWheelFrictionSlip(i, CAR.friction);
    }
    this.mesh = carMesh(this.wheels);
    if (model) {
      // The model's wheels are part of its body: hide the built-in body and wheels, sit it on the
      // wheels' contact line (hub height below the chassis, less the tyre radius).
      for (const c of this.mesh.children) c.visible = false;
      const body = new THREE.Mesh(model.geometry, model.material);
      body.position.y = CAR.wheelY - CAR.rest - CAR.wheelRadius + 0.08;
      body.castShadow = true;
      body.receiveShadow = true;
      this.mesh.add(body);
      // Lights on the model's tail: red outboard, reversing lights inboard.
      const g = model.geometry;
      if (!g.boundingBox) g.computeBoundingBox();
      const bb = g.boundingBox!;
      const y = body.position.y + bb.min.y + (bb.max.y - bb.min.y) * 0.52;
      // Set into the tail (rear ends are tapered) so only the lens faces show.
      const hw = ((bb.max.z - bb.min.z) / 2) * 0.6;
      for (const side of [-1, 1]) {
        const tail = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.07, 0.24), this.tailMat);
        tail.position.set(bb.min.x + 0.1, y, side * hw);
        const rev = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.06, 0.09), this.reverseMat);
        rev.position.set(bb.min.x + 0.1, y, side * (hw - 0.2));
        this.mesh.add(tail, rev);
      }
    }
    this.curP.copy(at);
    this.curQ.copy(q);
    this.prevP.copy(at);
    this.prevQ.copy(q);
  }

  /** Forward speed (m/s, negative in reverse). */
  get speed(): number {
    return this.vc.currentVehicleSpeed();
  }

  tick(h: number, input: CarInput): void {
    // A parked chassis falls asleep, and wheel forces don't wake it.
    if ((input.throttle !== 0 || input.steer !== 0) && this.body.isSleeping()) this.body.wakeUp();
    const v = this.speed;
    // Less lock at speed so it stays drivable.
    const target = input.steer * (CAR.steer / (1 + Math.abs(v) / 14));
    const ds = CAR.steerRate * h;
    this.steerAngle += Math.max(-ds, Math.min(ds, target - this.steerAngle));
    let engine = 0;
    let brake = 0;
    if (input.throttle > 0) {
      if (v < -0.5) brake = CAR.brake;
      else if (v < CAR.topSpeed) engine = CAR.engine * input.throttle;
    } else if (input.throttle < 0) {
      if (v > 0.5) brake = CAR.brake;
      else if (v > -CAR.topSpeed / 3) engine = -CAR.reverse * -input.throttle;
    } else brake = 2; // rolling resistance
    this.braking = brake > 2 || input.handbrake;
    this.reversing = input.throttle < 0 && v < 0.5;
    for (let i = 0; i < 4; i++) {
      const front = i < 2;
      this.vc.setWheelSteering(i, front ? this.steerAngle : 0);
      this.vc.setWheelEngineForce(i, front ? 0 : engine);
      this.vc.setWheelBrake(i, input.handbrake ? (front ? 0 : CAR.brake * 2) : brake);
    }
    this.vc.updateVehicle(h);
  }

  /** The hardest hit (m/s of velocity change in one step) since the last call. */
  takeImpact(): number {
    const v = this.impactMax;
    this.impactMax = 0;
    return v;
  }

  /** Sideways speed (m/s) along the axles: how much the car is sliding. */
  get lateral(): number {
    const v = this.body.linvel();
    const r = this.body.rotation();
    const axle = new THREE.Vector3(0, 0, 1).applyQuaternion(new THREE.Quaternion(r.x, r.y, r.z, r.w));
    return v.x * axle.x + v.y * axle.y + v.z * axle.z;
  }

  /** Wheels touching the ground. */
  get wheelsDown(): number {
    let n = 0;
    for (let i = 0; i < 4; i++) if (this.vc.wheelIsInContact(i)) n++;
    return n;
  }

  /** Record the pose after a world step (for interpolation). */
  capture(): void {
    const lv = this.body.linvel();
    const cur: [number, number, number] = [lv.x, lv.y, lv.z];
    this.impactMax = Math.max(this.impactMax, impactSpeed(this.lastVel, cur, this.phys.step));
    this.lastVel[0] = cur[0];
    this.lastVel[1] = cur[1];
    this.lastVel[2] = cur[2];
    this.prevP.copy(this.curP);
    this.prevQ.copy(this.curQ);
    const t = this.body.translation();
    const r = this.body.rotation();
    this.curP.set(t.x, t.y, t.z);
    this.curQ.set(r.x, r.y, r.z, r.w);
  }

  /** Place the model at the interpolated pose and turn the wheels. */
  sync(alpha: number): void {
    // Bright enough to bloom when braking (the threshold is about 9 outdoors).
    const on = this.lights;
    const brake = on && this.braking;
    this.tailMat.color.setRGB(brake ? 40 : on ? 2.5 : 0.3, brake ? 1.2 : on ? 0.06 : 0.02, brake ? 0.8 : on ? 0.04 : 0.02);
    this.reverseMat.color.setScalar(on && this.reversing ? 30 : 0.2);
    this.mesh.position.lerpVectors(this.prevP, this.curP, alpha);
    this.mesh.quaternion.slerpQuaternions(this.prevQ, this.curQ, alpha);
    for (let i = 0; i < 4; i++) {
      const w = this.wheels[i];
      const len = this.vc.wheelSuspensionLength(i) ?? CAR.rest;
      w.position.set(i < 2 ? CAR.wheelX : -CAR.wheelX, CAR.wheelY - len, i % 2 === 0 ? CAR.wheelZ : -CAR.wheelZ);
      w.rotation.set(0, this.vc.wheelSteering(i) ?? 0, 0, 'YXZ');
      (w.children[0] as THREE.Object3D).rotation.z = -(this.vc.wheelRotation(i) ?? 0);
    }
  }

  get position(): THREE.Vector3 {
    return this.mesh.position;
  }

  /** Heading (rad about +y) of the car's forward axis. */
  get yaw(): number {
    const f = new THREE.Vector3(1, 0, 0).applyQuaternion(this.mesh.quaternion);
    return Math.atan2(-f.z, f.x);
  }

  /** Put the car back on its wheels at `at` facing `yaw` (after a roll or when stuck). */
  reset(at: THREE.Vector3, yaw: number): void {
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    this.body.setTranslation(at, true);
    this.body.setRotation(q, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.lastVel.fill(0);
    this.impactMax = 0;
    this.curP.copy(at);
    this.curQ.copy(q);
    this.prevP.copy(at);
    this.prevQ.copy(q);
  }

  dispose(): void {
    this.tailMat.dispose();
    this.reverseMat.dispose();
    this.phys.world.removeVehicleController(this.vc);
    this.phys.world.removeRigidBody(this.body);
    this.mesh.removeFromParent();
    this.mesh.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
  }
}

const PAINT = new THREE.MeshStandardMaterial({ color: 0xa3242b, roughness: 0.35, metalness: 0.6 });
const GLASS = new THREE.MeshStandardMaterial({ color: 0x1b2430, roughness: 0.1, metalness: 0.7 });
const TYRE = new THREE.MeshStandardMaterial({ color: 0x1c1c1e, roughness: 0.9 });
const RIM = new THREE.MeshStandardMaterial({ color: 0xb8bcc2, roughness: 0.3, metalness: 0.9 });
const LAMP = new THREE.MeshStandardMaterial({ color: 0xfff4d6, emissive: 0xfff4d6, emissiveIntensity: 2 });
const TAIL = new THREE.MeshStandardMaterial({ color: 0x7a0c0c, emissive: 0x5a0000, emissiveIntensity: 1.5 });

/** Materials of the car model (to hook into the shadow setup). */
export const CAR_MATERIALS: THREE.Material[] = [PAINT, GLASS, TYRE, RIM, LAMP, TAIL];

/** A low-poly hatchback about the chassis box: body, cabin, lights, four wheels. */
function carMesh(wheels: THREE.Object3D[]): THREE.Group {
  const g = new THREE.Group();
  g.name = 'car';
  const box = (w: number, h: number, d: number, x: number, y: number, z: number, m: THREE.Material) => {
    const o = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
    o.position.set(x, y, z);
    o.castShadow = true;
    o.receiveShadow = true;
    g.add(o);
    return o;
  };
  const { x: hx, y: hy, z: hz } = CAR.half;
  box(hx * 2, hy * 1.6, hz * 2, 0, -hy * 0.2, 0, PAINT);
  box(hx * 1.05, hy * 1.4, hz * 1.8, -hx * 0.18, hy * 1.3, 0, GLASS);
  box(hx * 1.0, 0.06, hz * 1.84, -hx * 0.2, hy * 2.02, 0, PAINT);
  for (const s of [-1, 1]) {
    box(0.06, 0.14, 0.34, hx + 0.01, 0.05, s * hz * 0.66, LAMP);
    box(0.06, 0.14, 0.3, -hx - 0.01, 0.1, s * hz * 0.7, TAIL);
  }
  const tyre = new THREE.CylinderGeometry(CAR.wheelRadius, CAR.wheelRadius, 0.24, 14).rotateX(Math.PI / 2);
  const rim = new THREE.CylinderGeometry(CAR.wheelRadius * 0.6, CAR.wheelRadius * 0.6, 0.25, 8).rotateX(Math.PI / 2);
  for (let i = 0; i < 4; i++) {
    const pivot = new THREE.Object3D();
    const spin = new THREE.Group();
    const t = new THREE.Mesh(tyre, TYRE);
    t.castShadow = true;
    spin.add(t, new THREE.Mesh(rim, RIM));
    pivot.add(spin);
    g.add(pivot);
    wheels.push(pivot);
  }
  return g;
}
